import React, { useState } from 'react';
import { Lock, Check, Pencil, Trash2 } from 'lucide-react';
import { UNDERSTANDING_COPY as COPY, KIND_ORDER } from './copy';

// "What George knows about you" (George P4-2, PRD Phase 4 FR4-8): what the
// coach understands, grouped by kind, each with where it came from, and the
// facts the owner told the coach. The owner can confirm, correct or delete
// any of it, or start over. Data and actions come from
// SucceedoUnderstandingContainer.

const CHIP = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-label uppercase tracking-[0.04em]';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function groupItems(items = []) {
  const groups = {};
  items.forEach((item) => {
    (groups[item.kind_label] = groups[item.kind_label] || []).push(item);
  });
  return Object.keys(groups)
    .sort((a, b) => {
      const ia = KIND_ORDER.indexOf(a);
      const ib = KIND_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    })
    .map((label) => ({ label, items: groups[label] }));
}

function basisLabel(basis, coachName) {
  const label = COPY.basis[basis] || COPY.basis.inferred;
  return typeof label === 'function' ? label(coachName) : label;
}

function ItemCard({ item, coachName, busy, onConfirm, onCorrect, onForget }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.statement);
  const [problem, setProblem] = useState(null);

  // onCorrect resolves true, or what to change: shown under the editor, where
  // the owner is looking.
  const save = async () => {
    const result = await onCorrect(item.key, text);
    if (result === true) {
      setProblem(null);
      setEditing(false);
    } else {
      setProblem(typeof result === 'string' ? result : COPY.actionFailed);
    }
  };
  const cancel = () => { setEditing(false); setText(item.statement); setProblem(null); };

  return (
    <article className="border border-border rounded-xl p-4 bg-card flex flex-col gap-3" data-testid="understanding-item">
      {editing ? (
        <div className="flex flex-col gap-2">
          <label className="sr-only" htmlFor={`correct-${item.id}`}>{COPY.correctPlaceholder}</label>
          <textarea
            id={`correct-${item.id}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            maxLength={300}
            placeholder={COPY.correctPlaceholder}
            aria-invalid={!!problem}
            aria-describedby={problem ? `correct-error-${item.id}` : undefined}
            className="w-full rounded-lg border border-input bg-background p-2 text-[15px] text-foreground"
          />
          {problem && (
            <p id={`correct-error-${item.id}`} role="alert" className="m-0 text-[13px] text-coral-ink">{problem}</p>
          )}
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={save}
                    className="rounded-full bg-orange-cta text-white px-4 py-1.5 text-[13px] font-medium disabled:opacity-50">
              {COPY.save}
            </button>
            <button type="button" onClick={cancel}
                    className="rounded-full border border-border px-4 py-1.5 text-[13px] text-foreground">
              {COPY.cancel}
            </button>
          </div>
        </div>
      ) : (
        <p className="m-0 text-[15px] leading-[1.55] text-foreground">{item.statement}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className={`${CHIP} bg-muted text-muted-foreground`}>{basisLabel(item.basis, coachName)}</span>
        {item.status === 'confirmed' && <span className={`${CHIP} bg-muted text-teal-ink`}>{COPY.confirmed}</span>}
        {item.status === 'corrected' && <span className={`${CHIP} bg-muted text-teal-ink`}>{COPY.corrected}</span>}
        {item.private && (
          <span className={`${CHIP} bg-muted text-muted-foreground`} title={COPY.privateHint(coachName)}>
            <Lock size={11} aria-hidden="true" /> {COPY.private}
          </span>
        )}
        {item.source_labels.length > 0 && (
          <span className="text-[12px] text-muted-foreground">
            {COPY.from}: {item.source_labels.join(' · ')}
          </span>
        )}
      </div>

      {!editing && (
        <div className="flex flex-wrap gap-3 text-[13px]">
          {item.status !== 'confirmed' && (
            <button type="button" disabled={busy} onClick={() => onConfirm(item.key)}
                    className="inline-flex items-center gap-1 text-foreground hover:underline disabled:opacity-50">
              <Check size={14} aria-hidden="true" /> {COPY.thatsRight}
            </button>
          )}
          <button type="button" disabled={busy} onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1 text-foreground hover:underline disabled:opacity-50">
            <Pencil size={14} aria-hidden="true" /> {COPY.correct}
          </button>
          <button type="button" disabled={busy} onClick={() => onForget(item.key)}
                  className="inline-flex items-center gap-1 text-coral-ink hover:underline disabled:opacity-50">
            <Trash2 size={14} aria-hidden="true" /> {COPY.delete}
          </button>
        </div>
      )}
    </article>
  );
}

// copy: Succeedo Phase 5 passes the advisor wording; partners by default.
function Sharing({ sharing, coachName, busy, onUpdateSharing, available, copy = {} }) {
  const [chosen, setChosen] = useState(sharing.categories || []);
  const toggle = (key) => setChosen((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]));
  const briefs = sharing.briefs || [];

  return (
    <section className="flex flex-col gap-3" aria-labelledby={copy.id || 'sharing-heading'} data-testid={copy.id || 'sharing-section'}>
      <h2 id={copy.id || 'sharing-heading'} className="m-0 font-display font-light text-[20px] text-foreground">{copy.heading || COPY.sharingHeading}</h2>
      <p className="m-0 text-[14px] text-muted-foreground max-w-[60ch]">{copy.intro || COPY.sharingIntro(coachName)}</p>
      <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5">
        <legend className="sr-only">{copy.heading || COPY.sharingHeading}</legend>
        {(available || sharing.available || []).map((category) => (
          <label key={category.key} className="flex items-center gap-2.5 text-[14px] text-foreground cursor-pointer">
            <input
              type="checkbox"
              className="accent-[hsl(var(--succeedo-orange))]"
              checked={chosen.includes(category.key)}
              onChange={() => toggle(category.key)}
            />
            {category.label}
          </label>
        ))}
      </fieldset>
      <p className="m-0 text-[12px] text-muted-foreground">
        {sharing.active ? COPY.sharingSince(formatDate(sharing.granted_at)) : (copy.offNote || COPY.sharingOffNote)}
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy || chosen.length === 0} onClick={() => onUpdateSharing(chosen)}
                className="rounded-full bg-orange-cta text-white px-4 py-1.5 text-[13px] font-medium disabled:opacity-50">
          {COPY.sharingSave}
        </button>
        {sharing.active && (
          <button type="button" disabled={busy} onClick={() => { setChosen([]); onUpdateSharing([]); }}
                  className="rounded-full border border-border px-4 py-1.5 text-[13px] text-coral-ink disabled:opacity-50">
            {COPY.sharingOff}
          </button>
        )}
      </div>
      {briefs.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="m-0 font-label text-[12px] uppercase tracking-[0.06em] text-muted-foreground">{COPY.briefsHeading}</h3>
          <ul className="m-0 p-0 list-none flex flex-col divide-y divide-border border border-border rounded-xl bg-card">
            {briefs.map((b) => (
              <li key={b.id} className="p-4 flex flex-col gap-1" data-testid="brief-row">
                <span className="text-[13px] font-medium text-foreground">
                  {b.partner} · {formatDate(b.approved_at)}
                </span>
                <span className="text-[14px] text-muted-foreground whitespace-pre-line">{b.brief}</span>
                <span className="text-[12px] text-muted-foreground">
                  {b.reads > 0 ? COPY.briefReads(b.reads) : COPY.briefUnread}
                  {!b.showing && ` · ${COPY.briefHidden}`}
                </span>
              </li>
            ))}
          </ul>
          <p className="m-0 text-[12px] text-muted-foreground">{copy.cannotRecall || COPY.briefCannotRecall}</p>
        </div>
      )}
    </section>
  );
}

function ForgetEverything({ coachName, busy, onForgetEverything }) {
  const [asking, setAsking] = useState(false);
  return (
    <section className="border-t border-border pt-6 flex flex-col gap-3" aria-labelledby="forget-heading">
      <h2 id="forget-heading" className="m-0 font-display font-light text-[20px] text-foreground">{COPY.forgetHeading}</h2>
      <p className="m-0 text-[14px] text-muted-foreground max-w-[60ch]">{COPY.forgetIntro(coachName)}</p>
      {asking ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[14px] text-foreground">{COPY.forgetAsk}</span>
          <button type="button" disabled={busy}
                  onClick={async () => { await onForgetEverything(); setAsking(false); }}
                  className="rounded-full bg-coral-ink text-white px-4 py-1.5 text-[13px] font-medium disabled:opacity-50">
            {COPY.forgetConfirm}
          </button>
          <button type="button" onClick={() => setAsking(false)}
                  className="rounded-full border border-border px-4 py-1.5 text-[13px] text-foreground">
            {COPY.cancel}
          </button>
        </div>
      ) : (
        <div>
          <button type="button" onClick={() => setAsking(true)}
                  className="rounded-full border border-border px-4 py-1.5 text-[13px] text-coral-ink">
            {COPY.forgetButton}
          </button>
        </div>
      )}
    </section>
  );
}

export default function UnderstandingPage({
  understanding, loading = false, error = null, busy = false, actionError = null,
  onConfirm, onCorrect, onForget, onForgetMemory, onForgetEverything, onUpdateSharing,
}) {
  const coachName = understanding?.coach_name || 'George';
  const items = understanding?.items || [];
  const memories = understanding?.memories || [];
  const groups = groupItems(items);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <span className="font-label text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
          {COPY.eyebrow(coachName)}
        </span>
        <h1 className="m-0 font-display font-light text-[30px] leading-[1.15] tracking-[-0.02em] text-foreground">
          {COPY.heading(coachName)}
        </h1>
        <p className="m-0 text-[15px] leading-[1.6] text-muted-foreground">{COPY.intro(coachName)}</p>
        {understanding?.generated_at && (
          <p className="m-0 text-[12px] text-muted-foreground">{COPY.updated(formatDate(understanding.generated_at))}</p>
        )}
      </header>

      {error && <p className="text-[14px] text-coral-ink">{COPY.error}</p>}
      {!error && loading && <p className="text-[14px] text-muted-foreground">{COPY.loading}</p>}
      {actionError && <p role="alert" className="text-[14px] text-coral-ink">{actionError}</p>}

      {!error && !loading && items.length === 0 && memories.length === 0 && (
        <div className="border border-dashed border-border rounded-2xl p-6 text-[15px] text-muted-foreground">
          {COPY.empty(coachName)}
        </div>
      )}

      {groups.map((group) => (
        <section key={group.label} className="flex flex-col gap-3" aria-label={group.label}>
          <h2 className="m-0 font-label text-[12px] uppercase tracking-[0.06em] text-muted-foreground">{group.label}</h2>
          {group.items.map((item) => (
            <ItemCard key={item.id} item={item} coachName={coachName} busy={busy}
                      onConfirm={onConfirm} onCorrect={onCorrect} onForget={onForget} />
          ))}
        </section>
      ))}

      {memories.length > 0 && (
        <section className="flex flex-col gap-3" aria-labelledby="memories-heading">
          <h2 id="memories-heading" className="m-0 font-display font-light text-[20px] text-foreground">
            {COPY.memoriesHeading(coachName)}
          </h2>
          <p className="m-0 text-[13px] text-muted-foreground">{COPY.memoriesIntro(coachName)}</p>
          <ul className="m-0 p-0 list-none flex flex-col divide-y divide-border border border-border rounded-xl bg-card">
            {memories.map((memory) => (
              <li key={memory.id} className="flex items-start justify-between gap-4 p-4" data-testid="memory-row">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] font-medium text-foreground">
                    {memory.title}
                    {memory.private && (
                      <span className={`${CHIP} ml-2 bg-muted text-muted-foreground`}>
                        <Lock size={11} aria-hidden="true" /> {COPY.private}
                      </span>
                    )}
                  </span>
                  <span className="text-[14px] text-muted-foreground">{memory.content}</span>
                </div>
                <button type="button" disabled={busy} onClick={() => onForgetMemory(memory.id)}
                        aria-label={`${COPY.delete}: ${memory.title}`}
                        className="shrink-0 inline-flex items-center gap-1 text-[13px] text-coral-ink hover:underline disabled:opacity-50">
                  <Trash2 size={14} aria-hidden="true" /> {COPY.delete}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {understanding?.sharing && onUpdateSharing && (
        <Sharing sharing={understanding.sharing} coachName={coachName} busy={busy} onUpdateSharing={onUpdateSharing} />
      )}
      {understanding?.sharing?.advisor && onUpdateSharing && (
        <Sharing
          sharing={understanding.sharing.advisor}
          available={understanding.sharing.available}
          coachName={coachName}
          busy={busy}
          onUpdateSharing={(categories) => onUpdateSharing(categories, 'advisor')}
          copy={{
            id: 'advisor-sharing',
            heading: COPY.advisorSharingHeading(understanding.sharing.advisor.name),
            intro: COPY.advisorSharingIntro(coachName, understanding.sharing.advisor.name),
            offNote: COPY.advisorSharingOffNote,
            cannotRecall: COPY.advisorBriefCannotRecall,
          }}
        />
      )}

      {(items.length > 0 || memories.length > 0) && (
        <ForgetEverything coachName={coachName} busy={busy} onForgetEverything={onForgetEverything} />
      )}
    </div>
  );
}
